import type {TurnResponse} from '@wandb/weave/lib/WbAgent/WbAgentClient';

type ShellResult = {
  stdout?: string;
  stderr?: string;
  exit_code?: number;
};

export const isShellResult = (value: unknown): value is ShellResult => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const result = value as Record<string, unknown>;
  return (
    ('stdout' in result || 'stderr' in result || 'exit_code' in result) &&
    (result.stdout === undefined || typeof result.stdout === 'string') &&
    (result.stderr === undefined || typeof result.stderr === 'string') &&
    (result.exit_code === undefined || typeof result.exit_code === 'number')
  );
};

export const extractPythonCodeBlock = (command: string): string | null => {
  const match = command.match(
    /^python\s+-\s+<<-?['"]?(\w+)['"]?\n([\s\S]*?)\n\1\s*$/
  );
  return match ? match[2] : null;
};

const PROMPT_CONTEXT_RE = /^<PromptContext>[\s\S]*?<\/PromptContext>\s*/;
const INLINE_RUN_ID_RE =
  /\{run_id: "[^"]*"(?:,\s*run_display_name: "([^"]*)")?\}/g;
const CONTEXT_RUN_RE =
  /\{run_id: "([^"]*)"(?:,\s*run_display_name: "([^"]*)")?\}/g;

export type UserPrompt = TurnResponse['user_prompt'];
type UserPromptPart = Exclude<UserPrompt, string>[number];
type PromptTextPart = Extract<UserPromptPart, {type: 'text'}>;

export function encodeMention(name: string, displayName: string): string {
  return `{run_id: "${name}", run_display_name: "${displayName}"}`;
}

export function encodePromptContext(
  runs: ReadonlyArray<{name: string; displayName: string}>,
  userText: string
): string {
  if (runs.length === 0) {
    return userText;
  }
  const contextBlock = runs
    .map(r => encodeMention(r.name, r.displayName))
    .join('\n');
  return `<PromptContext> The following data is provided as supplementary to the user prompt\n${contextBlock}\n</PromptContext>\n${userText}`;
}

export function decodePromptForThreadTitle(raw: UserPrompt): string {
  return decodePromptMentions(promptToText(raw));
}

export function decodePromptForMessageDisplay(raw: string): string {
  return decodePromptMentions(raw, name => `\`${name}\``);
}

function decodePromptMentions(
  raw: string,
  formatMention: (displayName: string) => string = name => `@${name}`
): string {
  const contextMatch = raw.match(PROMPT_CONTEXT_RE);
  const contextNames: string[] = [];
  if (contextMatch) {
    for (const m of contextMatch[0].matchAll(CONTEXT_RUN_RE)) {
      if (m[2]) {
        contextNames.push(m[2]);
      }
    }
  }

  const cleaned = raw
    .replace(PROMPT_CONTEXT_RE, '')
    .replace(INLINE_RUN_ID_RE, (_, displayName) =>
      displayName ? formatMention(displayName) : ''
    )
    .replace(/  +/g, ' ')
    .trim();

  if (cleaned) {
    return cleaned;
  }
  // Handle edge case: if only run context tag
  if (contextNames.length > 0) {
    return contextNames.map(formatMention).join(', ');
  }
  return '';
}

function promptToText(raw: UserPrompt): string {
  if (typeof raw === 'string') {
    return raw;
  }
  if (!Array.isArray(raw)) {
    return '';
  }

  const textParts = raw.filter(isPromptTextPart).map(part => part.text);

  return textParts.join(' ').replace(/\s+/g, ' ').trim();
}

function isPromptTextPart(part: UserPromptPart): part is PromptTextPart {
  return part.type === 'text';
}
